import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';

import { ApiEndpointInvoke } from '@hsc/common/constants/endpoints';
import { BASE_APIGEE_URL } from '@constants/urls';
import { handleError } from '@utilities/http-utilities';

/**
 * Service for making HTTP requests to Apigee endpoints
 * Provides GET and POST methods with standardized error handling and base URL configuration
 */
@Injectable({ providedIn: 'root' })
export class ApigeeService {
  // Services
  private readonly http = inject(HttpClient);

  // Apigee Service Logic
  private readonly baseUrl = `${BASE_APIGEE_URL}/marketing-research/tsi-gateway-services-auth/v1/router`;
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds aligns with api gateway default
  // Private writable signal
  private readonly _isLoading = signal(false);
  // Exposed as read-only signal
  readonly isLoading = this._isLoading.asReadonly();

  /**
   * RxJS operator that manages loading state for HTTP requests
   * Sets loading to true on subscription and false on completion/error
   */
  private handleLoadingState() {
    return <T>(source: Observable<T>): Observable<T> => {
      this._isLoading.set(true);
      return source.pipe(finalize(() => this._isLoading.set(false)));
    };
  }

  /**
   * Performs GET request to Apigee endpoint
   * @param endpoint - API endpoint path
   * @param options - HTTP request options (headers, params, etc.)
   * @returns Observable of the response
   * @template T - Expected response type
   */
  get<T>(endpoint: ApiEndpointInvoke, options?: object): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/${endpoint}`, options).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      this.handleLoadingState(),
      catchError((error: HttpErrorResponse) => handleError(error)),
    );
  }

  /**
   * Performs POST request to Apigee endpoint
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @param options - HTTP request options (headers, params, etc.)
   * @returns Observable of the response
   * @template T - Expected response type
   */
  post<T>(endpoint: ApiEndpointInvoke, body: unknown, options?: object): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, options).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      this.handleLoadingState(),
      catchError((error: HttpErrorResponse) => handleError(error)),
    );
  }

  /**
   * Performs PUT request to Apigee endpoint
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @param options - HTTP request options (headers, params, etc.)
   * @returns Observable of the response
   * @template T - Expected response type
   */
  put<T>(endpoint: ApiEndpointInvoke, body: unknown, options?: object): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, body, options).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      this.handleLoadingState(),
      catchError((error: HttpErrorResponse) => handleError(error)),
    );
  }

  /**
   * Performs PATCH request to Apigee endpoint
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @param options - HTTP request options (headers, params, etc.)
   * @returns Observable of the response
   * @template T - Expected response type
   */
  patch<T>(endpoint: ApiEndpointInvoke, body: unknown, options?: object): Observable<T> {
    return this.http.patch<T>(`${this.baseUrl}/${endpoint}`, body, options).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      this.handleLoadingState(),
      catchError((error: HttpErrorResponse) => handleError(error)),
    );
  }

  /**
   * Performs DELETE request to Apigee endpoint
   * @param endpoint - API endpoint path
   * @param options - HTTP request options (headers, params, etc.)
   * @returns Observable of the response
   * @template T - Expected response type
   */
  delete<T>(endpoint: ApiEndpointInvoke, options?: object): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`, options).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      this.handleLoadingState(),
      catchError((error: HttpErrorResponse) => handleError(error)),
    );
  }
}
